import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProcessFormV3 from "./ProcessFormV3";
import { useToast } from "@/components/ui/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { processApi } from "@/services/api/endpoints";
import { Process } from "@/types/process";
import { format } from "date-fns";
import { PROCESS_STATUS } from "@/constants/process-status";

const parseBRLCurrency = (val: any) => {
  if (!val) return 0;
  if (typeof val === "number") return val;
  const strVal = String(val);
  return parseFloat(strVal.replace(/\./g, "").replace(",", "."));
};

const ProcessEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: process, isLoading, error } = useQuery<Process>({
    queryKey: ["process", id],
    queryFn: () => processApi.get(id!),
    enabled: !!id,
  });

  const updateProcess = useMutation({
    mutationFn: async (data: any) => {
      const payload = {
        name: data.code, // Using code as name since backend still requires it
        code: data.code,
        description: data.description,
        modality_id: data.modality_id,
        object: data.object,
        estimated_value: String(parseBRLCurrency(data.estimated_value)),
        publication_date: data.publication_date
          ? format(data.publication_date, "yyyy-MM-dd")
          : undefined,
        opening_date: data.opening_date
          ? format(data.opening_date, "yyyy-MM-dd")
          : undefined,
        opening_time: data.opening_time || null,
        responsible: data.responsible,
        status: data.status || PROCESS_STATUS.ABERTO,
      };

      const response = await processApi.update(id, payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["processes"] });
      queryClient.invalidateQueries({ queryKey: ["process", id] });
      toast({
        title: "Processo atualizado com sucesso!",
        description: "As alterações foram salvas.",
      });
      navigate(`/processes/${id}`);
    },
    onError: (error) => {
      console.error("Error updating process:", error);
      toast({
        title: "Erro ao atualizar processo",
        description: "Ocorreu um erro ao salvar as alterações.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (!id) return;

      const updateData = {
        ...values,
        modality_id: Number(values?.modality_id)
      };
      await updateProcess.mutateAsync(updateData);
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  if (error || !process) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground mb-4">
            Processo não encontrado
          </h2>
          <p className="text-muted-foreground mb-6">
            O processo que você está tentando editar não existe ou foi removido.
          </p>
          <button
            onClick={() => navigate("/processes")}
            className="gradient-primary px-4 py-2 rounded"
          >
            Voltar para lista
          </button>
        </div>
      </div>
    );
  }

  const initialValues = {
    code: process.code || "",
    description: process.description || "",
    modality_id: process.modality?.id || 0,
    object: process.object || "",
    estimated_value: process.estimated_value
      ? process.estimated_value.toString()
      : "",
    publication_date: process.publication_date
      ? new Date(process.publication_date)
      : null,
    opening_date: process.opening_date
      ? new Date(process.opening_date)
      : null,
    opening_time: process.opening_time || "",
    responsible: process.responsible || "",
    status: process.status || PROCESS_STATUS.ABERTO,
  };

  return (
    <ProcessFormV3
      initialValues={initialValues}
      onSubmit={onSubmit}
      isEditing={true}
      submitButtonText="Salvar Alterações"
    />
  );
};

export default ProcessEdit;
